import { type DriveFile } from '@/shared/DriveTypes';
import { useSWRConfig } from 'swr';
import { Link } from 'wouter';
import Icon from '~/elements/Icon/Icon';
import { LEGACY, search } from '~/utils/constants';
import gup from '~/utils/gup';
import { isMarkdownLinkTitle } from '~/utils/isMarkdownLink';
import { prefetch } from '~/utils/prefetch';
import { sort } from '~/utils/sort';
import { isADocument, legacyClick } from '~/utils/utils';
import * as styles from './StaticTree.css';

const getHref = (file: DriveFile, wiki: DriveFile, externalLink: { url: string; name: string } | undefined) => {
  if (externalLink?.url) return externalLink.url;

  const legacyParentId = LEGACY ? gup('p') : undefined;
  const legacyTo = LEGACY ? (legacyParentId ? `/app/page/${file.id}?p=${legacyParentId}` : `/app/page/${file.id}`) : '';

  return LEGACY ? legacyTo : `/app/page/${wiki.id}/${file.id}${search}`;
};

/**
 * Static version for pre-rendering
 * Do not use context or hooks
 */
export const StaticTreeMarkup = ({ files, wiki }: { files: DriveFile[]; wiki: DriveFile }) => {
  const { fontColor } = wiki.properties ?? {};
  if (files.length === 0) return null;

  return (
    <div role="tree">
      {sort(files).map((file) => {
        const externalLink = isADocument(file) ? isMarkdownLinkTitle(file.name) : undefined;
        const href = getHref(file, wiki, externalLink);
        return (
          <div role="treeitem" key={file.id} className={styles.linkContainer}>
            <a className={styles.link} href={href} {...(externalLink?.url && { target: '_blank' })}>
              <Icon file={file} color={fontColor} externalLink={externalLink} />
              <span className={styles.textContainer}>
                <span className={styles.text}>{externalLink ? externalLink.name : file.name}</span>
              </span>
            </a>
          </div>
        );
      })}
    </div>
  );
};

export const StaticTree = ({ files, wiki }: { files: DriveFile[]; wiki: DriveFile }) => {
  const { cache } = useSWRConfig();
  const { fontColor } = wiki.properties ?? {};
  if (files.length === 0) return null;

  return (
    <div role="tree">
      {sort(files).map((file) => {
        const externalLink = isADocument(file) ? isMarkdownLinkTitle(file.name) : undefined;
        const href = getHref(file, wiki, externalLink);
        return (
          <div role="treeitem" key={file.id} className={styles.linkContainer} onMouseDown={() => prefetch(file, cache)}>
            <Link
              className={styles.link}
              to={href}
              {...(externalLink?.url && { target: '_blank' })}
              onClick={legacyClick}
            >
              <Icon file={file} color={fontColor} externalLink={externalLink} />
              <span className={styles.textContainer}>
                <span className={styles.text}>{externalLink ? externalLink.name : file.name}</span>
              </span>
            </Link>
          </div>
        );
      })}
    </div>
  );
};
